import { generateItems } from "game-common/item/item"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { Appearance, Entity, Inventory, pctChance } from "game-common/models"
import { degrees_to_radians } from "game-common/util"
import { Container, Graphics } from "pixi.js"

import { LpcComposite } from "../../sprites/lpc_composite"
import { FlameBackdrop } from "../flame_backdrop"
import { BuilderPage, PageId } from "./page"
import { GridLayout, cellPosition, layoutGrid, pageSlice } from "./grid"
import {
    ACCENT,
    BODY_H,
    BODY_Y,
    PALETTE_W,
    PALETTE_X,
    STAGE_W,
    STAGE_X,
    TEXT_DIM,
    TRAITS_W,
    TRAITS_X,
} from "./theme"
import { TraitId, TraitSection, applyTraitChoice, describeTrait, describeTraits, reconcileForSex } from "./traits"
import { ColorChip, LabelChip, Pager, TraitRow, drawGlassPanel, fitText, sectionHeading, text } from "./widgets"

// The character forge: the trait list on the left, the character on a lit stage in the middle, and
// the palette for whichever trait is open on the right.
//
// Two things drive the design. The character always faces the viewer -- you are looking at the face
// you are building, not at whichever way the mouse last pointed. And every trait opens as a PALETTE:
// all of its choices laid out at once as chips and colour swatches, one click each, instead of a
// pair of arrows to cycle through.

const PANEL_PAD = 8
const ROW_H = 36
const ROW_GAP = 3

const PALETTE_PAD = 14
const PALETTE_INNER_W = PALETTE_W - PALETTE_PAD * 2
const PALETTE_TITLE_H = 38

const STYLE_CELL_H = 24
const STYLE_MIN_W = 76
const COLOR_CELL = 24
const GRID_GAP = 6
const SECTION_HEAD_H = 14
const SECTION_GAP = 10
const PAGER_H = 22

// The stage. The composite is drawn from the centre of its 64x64 frame, and a standing LPC figure's
// feet sit about 28px below that centre.
const COMPOSITE_SCALE = 3.4
const FEET_OFFSET = 28 * COMPOSITE_SCALE
const FEET_Y = BODY_Y + 252
const STAGE_CENTER_X = STAGE_X + STAGE_W / 2
const FLAME_H = 132

export class BuildPage extends BuilderPage {
    readonly pageId: PageId = "build"
    readonly title = "Forge your character"
    readonly hint = "Pick a trait, then pick from its palette — the character changes at once"

    // A private copy of the player: nothing the screen does touches the live entity until the
    // character is submitted.
    private entity: Entity
    private composite: LpcComposite
    private flame: FlameBackdrop

    private openTrait: TraitId = "sex"
    // Palettes bigger than one page remember where each trait was left.
    private pageIndex: Partial<Record<TraitId, number>> = {}

    constructor(player: Entity) {
        super()
        this.entity = this.cloneForBuilder(player)
        this.flame = new FlameBackdrop(STAGE_W - 24, FLAME_H)
    }

    // Deep enough that editing the draft can't reach back into the live player's inventory (a
    // shallow copy shares the items array, so a new shirt would land on the player mid-creation).
    private cloneForBuilder = (player: Entity): Entity => {
        const entity = new Entity()
        entity.movement.angle = degrees_to_radians(90)
        entity.weapon = { weaponType: "none" }
        entity.appearance = { ...(player.appearance || randomAppearanceFor(undefined, "human", "male")) }
        entity.inventory = {
            items: (player.inventory?.items || []).map(item => ({
                ...item,
                itemTypeMeta: item.itemTypeMeta ? { ...item.itemTypeMeta } : undefined,
            })),
            abilities: { ...(player.inventory?.abilities || {}) },
            lastUpdateTs: 0,
        }

        const appearance = entity.appearance
        appearance.race = "human"
        // The eye layer is what the "Eyes" palette tints; an appearance that arrived without one has
        // nothing to colour.
        appearance.eyes = appearance.eyes || "normal"
        appearance.eyesColor = appearance.eyesColor || "brown"
        appearance.noseColor = appearance.bodyColor
        if (entity.inventory.items.length < 1) {
            entity.inventory.items = this.rollOutfit(entity)
        }
        reconcileForSex(entity)
        return entity
    }

    private rollOutfit = (entity: Entity) =>
        generateItems(entity.appearance.sex, {
            pants: true,
            shirt: entity.appearance.sex === "male" ? pctChance(60) : true,
            shoe: true,
            weapon: true,
        })

    getAppearance = (): Appearance => this.entity.appearance
    getInventory = (): Inventory => this.entity.inventory

    animate(dtMs: number) {
        // A low, steady hearth glow at the character's feet -- alive, but never competing with the
        // character for attention.
        this.flame.update(dtMs, 0.45)
    }

    tearDown() {
        this.openTrait = "sex"
    }

    // Detach the pieces that outlive a repaint before the rest of the page is disposed.
    protected wipe() {
        if (this.composite?.parent) {
            this.composite.parent.removeChild(this.composite)
        }
        if (this.flame?.parent) {
            this.flame.parent.removeChild(this.flame)
        }
        super.wipe()
    }

    build() {
        this.wipe()
        this.drawTraitList()
        this.drawStage()
        this.drawPalette()
        this.drawNav()
    }

    private drawTraitList() {
        const panel = new Graphics()
        drawGlassPanel(panel, TRAITS_X, BODY_Y, TRAITS_W, BODY_H)
        this.addChild(panel)

        const views = describeTraits(this.entity)
        const listH = views.length * ROW_H + (views.length - 1) * ROW_GAP
        let y = BODY_Y + Math.max(PANEL_PAD, (BODY_H - listH) / 2)

        views.forEach(view => {
            const row = new TraitRow({
                icon: view.icon,
                label: view.label,
                summary: view.summary,
                swatch: view.swatch,
                width: TRAITS_W - PANEL_PAD * 2,
                height: ROW_H,
                selected: view.id === this.openTrait,
                onClick: () => {
                    this.openTrait = view.id
                    this.host.refresh()
                },
            })
            row.x = TRAITS_X + PANEL_PAD
            row.y = y
            this.addChild(row)
            y += ROW_H + ROW_GAP
        })
    }

    private drawStage() {
        const panel = new Graphics()
        drawGlassPanel(panel, STAGE_X, BODY_Y, STAGE_W, BODY_H)
        this.addChild(panel)

        // Warm pool of light the character stands in, drawn as a few stacked ellipses (cheaper than
        // a blur, and it reads as firelight from below).
        const glow = new Graphics()
        glow.beginFill(0xff8a24, 0.07)
        glow.drawEllipse(STAGE_CENTER_X, FEET_Y - 20, 96, 104)
        glow.endFill()
        glow.beginFill(0xffcf5a, 0.06)
        glow.drawEllipse(STAGE_CENTER_X, FEET_Y - 6, 62, 62)
        glow.endFill()
        this.addChild(glow)

        this.flame.x = STAGE_X + 12
        this.flame.y = BODY_Y + BODY_H - FLAME_H - 6
        this.addChild(this.flame)

        // Ground shadow, so the figure stands on something.
        const floor = new Graphics()
        floor.beginFill(0x000000, 0.45)
        floor.drawEllipse(STAGE_CENTER_X, FEET_Y + 2, 46, 11)
        floor.endFill()
        floor.beginFill(0x000000, 0.3)
        floor.drawEllipse(STAGE_CENTER_X, FEET_Y + 2, 62, 15)
        floor.endFill()
        this.addChild(floor)

        if (!this.composite) {
            this.composite = new LpcComposite("")
            this.composite.scale.set(COMPOSITE_SCALE)
        }
        this.composite.updatePaperdoll(this.entity)
        // Always facing the viewer: the angle is pinned to "front" and nothing turns it.
        this.composite.doMovement(this.entity)
        this.composite.x = STAGE_CENTER_X
        this.composite.y = FEET_Y - FEET_OFFSET
        this.addChild(this.composite)

        const caption = text("Bound for the West", { size: 9, color: TEXT_DIM })
        caption.x = Math.round(STAGE_CENTER_X - caption.width / 2)
        caption.y = BODY_Y + BODY_H - 20
        caption.alpha = 0.8
        this.addChild(caption)
    }

    private drawPalette() {
        const panel = new Graphics()
        drawGlassPanel(panel, PALETTE_X, BODY_Y, PALETTE_W, BODY_H)
        this.addChild(panel)

        const view = describeTrait(this.entity, this.openTrait)
        const x = PALETTE_X + PALETTE_PAD

        const heading = text(view.label, { size: 15, color: ACCENT, bold: true })
        heading.x = x
        heading.y = BODY_Y + 11
        this.addChild(heading)

        const hint = text(`Now: ${view.summary}`, { size: 10, color: TEXT_DIM })
        fitText(hint, PALETTE_INNER_W - heading.width - 14)
        hint.x = PALETTE_X + PALETTE_W - PALETTE_PAD - hint.width
        hint.y = BODY_Y + 15
        this.addChild(hint)

        const top = BODY_Y + PALETTE_TITLE_H
        const bottom = BODY_Y + BODY_H - PALETTE_PAD
        const colorSection = view.sections.find(section => section.kind === "color")
        const styleSection = view.sections.find(section => section.kind === "style")

        // The colour row is short and fixed; the style grid takes whatever is left.
        let colorBlockH = 0
        let colorLayout: GridLayout | undefined
        if (colorSection) {
            colorLayout = layoutGrid({
                count: colorSection.options.length,
                width: PALETTE_INNER_W,
                height: COLOR_CELL * 2 + GRID_GAP,
                cellHeight: COLOR_CELL,
                minCellWidth: COLOR_CELL,
                maxCellWidth: COLOR_CELL + 6,
                gapX: GRID_GAP,
                gapY: GRID_GAP,
            })
            colorBlockH = SECTION_HEAD_H + colorLayout.height + SECTION_GAP
        }

        let y = top
        if (styleSection) {
            const available = bottom - top - colorBlockH - SECTION_HEAD_H - PAGER_H
            const layout = layoutGrid({
                count: styleSection.options.length,
                width: PALETTE_INNER_W,
                height: available,
                cellHeight: STYLE_CELL_H,
                minCellWidth: STYLE_MIN_W,
                gapX: GRID_GAP,
                gapY: GRID_GAP,
            })
            y = this.drawSection(styleSection, x, y, layout)
        }

        if (colorSection && colorLayout) {
            // With a style grid above it the colour row sits on the floor of the panel; on its own
            // (skin, eyes) it belongs at the top, not stranded at the bottom of an empty panel.
            const colorY = styleSection ? Math.max(y, bottom - colorBlockH + SECTION_GAP) : top
            this.drawSection(colorSection, x, colorY, colorLayout)
        }
    }

    // Draws one section (heading, grid of choices, pager when it doesn't fit on a page) and returns
    // the y just below it.
    private drawSection(section: TraitSection, x: number, y: number, layout: GridLayout): number {
        const heading = sectionHeading(section.title, PALETTE_INNER_W)
        heading.x = x
        heading.y = y
        this.addChild(heading)

        const gridY = y + SECTION_HEAD_H
        const page = Math.min(this.pageIndex[this.openTrait] || 0, layout.pages - 1)
        const shown = pageSlice(section.options, layout, page)
        const cellH = section.kind === "color" ? COLOR_CELL : STYLE_CELL_H

        shown.forEach((option, index) => {
            const at = cellPosition(index, layout, cellH, GRID_GAP, GRID_GAP)
            const onClick = () => this.choose(section, option.value)
            const chip: Container =
                section.kind === "color"
                    ? new ColorChip(option.swatch, {
                          width: layout.cellWidth,
                          height: cellH,
                          selected: option.value === section.selected,
                          onClick,
                      })
                    : new LabelChip(option.label, {
                          width: layout.cellWidth,
                          height: cellH,
                          selected: option.value === section.selected,
                          onClick,
                      })
            chip.x = x + at.x
            chip.y = gridY + at.y
            this.addChild(chip)
        })

        let bottom = gridY + layout.height + SECTION_GAP

        if (layout.pages > 1) {
            const pager = new Pager({
                page,
                pages: layout.pages,
                onPage: next => {
                    this.pageIndex[this.openTrait] = next
                    this.host.refresh()
                },
            })
            pager.x = x + PALETTE_INNER_W - pager.boxWidth
            pager.y = gridY + layout.height + 4
            this.addChild(pager)
            bottom += PAGER_H
        }

        return bottom
    }

    private choose(section: TraitSection, value: string) {
        applyTraitChoice(this.entity, this.openTrait, section.kind, value)
        // A different hairstyle (or cut of shirt) can offer a different set of colours, so the open
        // palette is rebuilt rather than patched.
        this.host.refresh()
    }

    private drawNav() {
        this.host.setNavButtons([
            {
                title: "🎲 Random look",
                callback: () => {
                    const appearance = randomAppearanceFor(this.entity, "human", this.entity.appearance.sex)
                    appearance.inventoryAppearanceHash = this.entity.appearance.inventoryAppearanceHash
                    this.entity.appearance = appearance
                    this.entity.appearance.eyes = this.entity.appearance.eyes || "normal"
                    this.entity.appearance.noseColor = this.entity.appearance.bodyColor
                    reconcileForSex(this.entity)
                    this.host.refresh()
                },
            },
            {
                title: "👕 Random outfit",
                callback: () => {
                    this.entity.inventory.items = this.rollOutfit(this.entity)
                    reconcileForSex(this.entity)
                    this.host.refresh()
                },
            },
            {
                title: "Take the road ▸",
                primary: true,
                callback: () => {
                    this.host.submitCharacter(this.entity.appearance, this.entity.inventory)
                    this.host.resetNavButtons()
                    this.host.switchPage("journey")
                },
            },
        ])
    }
}
